import React, { useState } from "react";
import {
  makeStyles,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  Box,
  Button,
  CircularProgress,
  Menu,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  useMediaQuery, // Import useMediaQuery
  useTheme, // Import useTheme
} from "@material-ui/core";
import FormatBoldIcon from "@material-ui/icons/FormatBold";
import FormatItalicIcon from "@material-ui/icons/FormatItalic";
import FormatAlignLeftIcon from "@material-ui/icons/FormatAlignLeft";
import FormatAlignCenterIcon from "@material-ui/icons/FormatAlignCenter";
import FormatAlignRightIcon from "@material-ui/icons/FormatAlignRight";
import FormatListBulletedIcon from "@material-ui/icons/FormatListBulleted";
import EditIcon from "@material-ui/icons/Edit"; // AI Edit Icon
import UndoIcon from "@material-ui/icons/Undo";
import RedoIcon from "@material-ui/icons/Redo";
import LinkIcon from "@material-ui/icons/Link";
import FormatColorTextIcon from "@material-ui/icons/FormatColorText";
import TitleIcon from "@material-ui/icons/Title";
import SubtitlesIcon from "@material-ui/icons/Subtitles";
import TextFieldsIcon from "@material-ui/icons/TextFields";
import SearchIcon from "@material-ui/icons/Search"; // Keep for now if used elsewhere, or remove if only for keyword
import HighlightIcon from "@material-ui/icons/Highlight"; // New Icon for keyword highlighting
import GetAppIcon from "@material-ui/icons/GetApp";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import FormatSizeIcon from "@material-ui/icons/FormatSize";
import AutorenewIcon from "@material-ui/icons/Autorenew"; // Reverting to default AI edit icon

import ColorPicker from "./ColorPicker";
import LinkPopover from "./LinkPopover";

const useStyles = makeStyles((theme) => ({
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    borderBottom: `1px solid ${theme.palette.divider}`,
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75),
      gap: theme.spacing(0.25),
      overflowX: "hidden", // Prevent scrolling, rely on wrapping
      flexWrap: "wrap", // Allow buttons to wrap
      justifyContent: "flex-start", // Align items to the start
      // "&::-webkit-scrollbar": { // No longer needed if not scrolling
      //   height: "4px",
      // },
      // "&::-webkit-scrollbar-thumb": {
      //   backgroundColor: theme.palette.divider,
      //   borderRadius: "4px",
      // },
      // Mobile-specific styles for fixed positioning
      position: "fixed",
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 10000, // Higher z-index to ensure it's above the keyboard
      boxShadow: "0 -3px 10px rgba(0,0,0,0.2)",
      backdropFilter: "blur(10px)",
      WebkitBackdropFilter: "blur(10px)",
      borderTop: `1px solid ${theme.palette.divider}`,
      borderBottom: "none",
    },
  },
  // Special class for when keyboard is visible
  toolbarWithKeyboard: {
    [theme.breakpoints.down("sm")]: {
      position: "fixed",
      bottom: "auto", // Remove bottom positioning
      top: "calc(100% - 56px - 270px)", // Position above typical keyboard height
    },
  },
  // Container for mobile toolbar to ensure proper spacing
  mobileToolbarContainer: {
    [theme.breakpoints.down("sm")]: {
      minHeight: "60px", // Revert closer to original, allow for 1-2 rows max.
      width: "100%",
      display: "none", // Hidden by default
    },
    [theme.breakpoints.up("md")]: {
      display: "none", // Always hidden on desktop
    },
  },
  // Show the container when the toolbar is fixed
  mobileToolbarContainerVisible: {
    [theme.breakpoints.down("sm")]: {
      display: "block",
    },
  },
  toolbarInnerContainer: {
    // New class for the main inner div
    display: "flex",
    flexWrap: "wrap", // Allow wrapping for all content within
    gap: theme.spacing(0.5), // Revert gap
    width: "100%",
    justifyContent: "center", // Center items horizontally
    alignItems: "center", // Align items vertically
  },
  buttonGroupsContainer: {
    // New class for the div holding button groups
    display: "flex",
    flexWrap: "wrap", // Allow button groups to wrap
    gap: theme.spacing(0.5), // Revert gap
    alignItems: "center",
    justifyContent: "center", // Center button groups/items within this container
    flexGrow: 1, // Allow this section to take available space
  },
  actionsContainer: {
    // New class for download/ATS buttons
    display: "flex",
    alignItems: "center",
    flexShrink: 0, // Prevent this section from shrinking
    gap: theme.spacing(0.5),
  },
  divider: {
    height: 24,
    margin: theme.spacing(0, 0.5),
    [theme.breakpoints.down("sm")]: {
      margin: theme.spacing(0, 0.25),
    },
  },
  group: {
    display: "flex",
    alignItems: "center",
    [theme.breakpoints.down("sm")]: {
      flexShrink: 0,
    },
  },
  activeButton: {
    backgroundColor: theme.palette.action.selected,
  },
  // Make buttons more touch-friendly on mobile
  iconButton: {
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1), // Increased padding for larger touch target (8px)
      margin: theme.spacing(0, 0.5), // Increased margin for more horizontal spacing (0px T/B, 4px L/R)
      minWidth: 44, // Minimum 44px tappable area
      minHeight: 44, // Minimum 44px tappable area
    },
  },
  // Style for the download button on mobile
  downloadButton: {
    [theme.breakpoints.down("sm")]: {
      marginLeft: "auto",
      flexShrink: 0,
      minWidth: "auto",
      padding: theme.spacing(0.5, 1),
      fontSize: "0.75rem",
    },
  },
  // ATS Analysis button
  atsButton: {
    marginLeft: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
    [theme.breakpoints.down("sm")]: {
      marginLeft: theme.spacing(0.5),
      padding: theme.spacing(0.75, 1), // Adjusted padding for text button
      fontSize: "0.75rem", // Mobile friendly font size
      // minWidth: 36, // Not needed for text button
      // minHeight: 36, // Not needed for text button
    },
  },
}));

/**
 * EditorToolbar Component
 *
 * A toolbar with formatting controls for the HTML editor
 *
 * @param {Object} props - Component props
 * @param {Function} props.onFormatText - Callback for text formatting
 * @param {Function} props.onUndo - Callback for undo
 * @param {Function} props.onRedo - Callback for redo
 * @param {boolean} props.canUndo - Whether undo is available
 * @param {boolean} props.canRedo - Whether redo is available
 * @param {Function} props.onLinkClick - Callback for link button click
 * @param {Function} props.onInsertLink - Callback for link insertion
 * @param {Function} props.onColorClick - Callback for color button click
 * @param {Function} props.onColorChange - Callback for color selection
 * @param {Function} props.onToggleKeywordHighlight - Callback for toggling keyword highlighting
 * @param {boolean} props.keywordHighlightEnabled - Whether keyword highlighting is enabled
 * @param {Function} props.onDownload - Callback for when the download button is clicked
 * @param {boolean} props.downloading - Whether a download is in progress
 * @param {boolean} props.hasContent - Whether there is content to download
 * @param {Function} props.onMobileZoomIn - Callback for mobile zoom in
 * @param {Function} props.onMobileZoomOut - Callback for mobile zoom out
 * @param {Function} props.onMobileResetZoom - Callback for mobile zoom reset
 * @param {number} props.currentScale - Current zoom scale
 * @param {boolean} props.showMobileControls - Whether to show mobile controls
 * @param {Function} props.onScrollToATS - Callback for scrolling to ATS Analysis
 * @param {string} props.documentType - Type of document being edited ('resume' or 'coverLetter')
 * @param {Function} props.getCurrentDocumentJson - Function to get current document as JSON
 * @param {Function} props.onUpdateDocumentFromJson - Function to update document from AI modified JSON
 * @returns {JSX.Element} The rendered component
 */
const EditorToolbar = ({
  onFormatText,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onLinkClick,
  onInsertLink,
  onColorClick,
  onColorChange,
  onToggleKeywordHighlight,
  keywordHighlightEnabled = false,
  onDownload,
  downloading = false,
  hasContent = false,
  onMobileZoomIn,
  onMobileZoomOut,
  onMobileResetZoom,
  currentScale,
  showMobileControls = false,
  onScrollToATS,
  documentType = "resume", // Default to resume for safety
  getCurrentDocumentJson,
  onUpdateDocumentFromJson,
}) => {
  const classes = useStyles();
  const theme = useTheme(); // Get theme for breakpoint check
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // Check if mobile

  const [linkPopoverAnchor, setLinkPopoverAnchor] = useState(null);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [headingMenuAnchor, setHeadingMenuAnchor] = useState(null);
  const [alignmentMenuAnchor, setAlignmentMenuAnchor] = useState(null); // State for alignment menu
  const [aiPromptOpen, setAiPromptOpen] = useState(false);
  const [aiPromptText, setAiPromptText] = useState("");
  const [isEditingWithAI, setIsEditingWithAI] = useState(false);

  const handleOpenAIPrompt = () => {
    setAiPromptOpen(true);
  };

  const handleCloseAIPrompt = () => {
    setAiPromptOpen(false);
    setAiPromptText("");
  };

  const handleSubmitAIPrompt = async () => {
    if (
      !aiPromptText.trim() ||
      !getCurrentDocumentJson || // Use new prop name
      !onUpdateDocumentFromJson // Use new prop name
    ) {
      console.error(
        "AI Edit Error: Prompt is empty or necessary functions (getCurrentDocumentJson, onUpdateDocumentFromJson) are not provided."
      );
      alert("Cannot perform AI edit. Required functions are missing.");
      return;
    }
    setIsEditingWithAI(true);
    try {
      const currentDocument = await getCurrentDocumentJson(); // Use new prop name

      const isResume = documentType === "resume";
      // Define URLs based on document type
      const apiUrl = isResume
        ? "https://prod-edit-resume-with-ai-660323987151.us-central1.run.app"
        : "https://prod-edit-cover-letter-with-ai-660323987151.us-central1.run.app";

      // Define payload and response keys based on document type
      const payloadKey = isResume
        ? "currentResumeJson"
        : "currentCoverLetterJson";
      const responseKey = isResume
        ? "editedResumeJson"
        : "editedCoverLetterJson";

      const payload = {
        [payloadKey]: currentDocument,
        userPrompt: aiPromptText,
      };

      console.log(`Sending AI edit request to ${apiUrl} for ${documentType}`);

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        let errorMsg = `HTTP error! status: ${response.status}`;
        try {
          // Try to parse error message from response body
          const errorData = await response.json();
          errorMsg = errorData.error || errorMsg;
        } catch (e) {
          // Ignore if response body is not JSON or empty
        }
        throw new Error(errorMsg);
      }

      const result = await response.json();

      // Use the dynamic response key
      if (result[responseKey]) {
        onUpdateDocumentFromJson(result[responseKey]); // Use new prop name
      } else {
        throw new Error(
          `AI edit response did not contain expected key '${responseKey}'.`
        );
      }
    } catch (error) {
      console.error(`Error editing ${documentType} with AI:`, error);
      alert(`Error editing ${documentType} with AI: ${error.message}`);
    } finally {
      setIsEditingWithAI(false);
      handleCloseAIPrompt();
    }
  };

  // Handle link button click
  const handleLinkClick = (event) => {
    setLinkPopoverAnchor(event.currentTarget);
    if (onLinkClick) {
      onLinkClick();
    }
  };

  // Handle color button click
  const handleColorClick = (event) => {
    setColorPickerAnchor(event.currentTarget);
    if (onColorClick) {
      onColorClick();
    }
  };

  // Close link popover
  const handleCloseLinkPopover = () => {
    setLinkPopoverAnchor(null);
  };

  // Close color picker
  const handleCloseColorPicker = () => {
    setColorPickerAnchor(null);
  };

  // Handle link insertion
  const handleInsertLink = (linkText, linkUrl) => {
    if (onInsertLink) {
      onInsertLink(linkText, linkUrl);
    }
    handleCloseLinkPopover();
  };

  // Handle color selection
  const handleColorChange = (color) => {
    if (onColorChange) {
      onColorChange(color);
    }
    handleCloseColorPicker();
  };

  // Handle heading menu click
  const handleHeadingMenuClick = (event) => {
    setHeadingMenuAnchor(event.currentTarget);
  };

  // Handle heading menu close
  const handleHeadingMenuClose = () => {
    setHeadingMenuAnchor(null);
  };

  // Handle heading format selection
  const handleHeadingFormat = (format) => {
    onFormatText(format);
    handleHeadingMenuClose();
  };

  // --- Alignment Menu Handlers ---
  const handleAlignmentMenuClick = (event) => {
    setAlignmentMenuAnchor(event.currentTarget);
  };

  const handleAlignmentMenuClose = () => {
    setAlignmentMenuAnchor(null);
  };

  const handleAlignmentFormat = (format) => {
    onFormatText(format);
    handleAlignmentMenuClose();
  };
  // --- End Alignment Menu Handlers ---

  // Create a ref for the toolbar container
  const toolbarRef = React.useRef(null);

  // State to track if the toolbar is visible and if keyboard is visible
  const [isToolbarVisible, setIsToolbarVisible] = React.useState(false);
  const [isKeyboardVisible, setIsKeyboardVisible] = React.useState(false);

  // Effect to show the container when the toolbar is fixed
  React.useEffect(() => {
    setIsToolbarVisible(showMobileControls);
  }, [showMobileControls]);

  // Effect to detect keyboard visibility on mobile
  React.useEffect(() => {
    if (!showMobileControls) return; // Only run on mobile

    // Function to check if keyboard is likely visible based on viewport height changes
    const checkKeyboard = () => {
      // Most mobile keyboards take up about 40% of the screen height
      const viewportHeight =
        window.visualViewport?.height || window.innerHeight;
      const windowHeight = window.innerHeight;

      // If viewport height is significantly less than window height, keyboard is likely visible
      const keyboardThreshold = windowHeight * 0.75;
      const isKeyboardOpen = viewportHeight < keyboardThreshold;

      setIsKeyboardVisible(isKeyboardOpen);
    };

    // Use visualViewport API if available (modern browsers)
    if (window.visualViewport) {
      window.visualViewport.addEventListener("resize", checkKeyboard);
    } else {
      // Fallback to window resize event
      window.addEventListener("resize", checkKeyboard);
    }

    // Initial check
    checkKeyboard();

    // Cleanup
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener("resize", checkKeyboard);
      } else {
        window.removeEventListener("resize", checkKeyboard);
      }
    };
  }, [showMobileControls]);

  return (
    <>
      {/* Spacer div that takes up space when the toolbar is fixed */}
      <div
        className={`${classes.mobileToolbarContainer} ${
          isToolbarVisible ? classes.mobileToolbarContainerVisible : ""
        }`}
      />

      <Paper
        className={`${classes.toolbar} ${
          isKeyboardVisible ? classes.toolbarWithKeyboard : ""
        }`}
        elevation={0}
        square
        ref={toolbarRef}
      >
        <div className={classes.toolbarInnerContainer}>
          <div className={classes.buttonGroupsContainer}>
            {/* History controls */}
            <Box className={classes.group}>
              <Tooltip title="Undo">
                <span>
                  <IconButton
                    size="small"
                    onClick={onUndo}
                    disabled={!canUndo}
                    aria-label="Undo"
                    className={classes.iconButton}
                  >
                    <UndoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Redo">
                <span>
                  <IconButton
                    size="small"
                    onClick={onRedo}
                    disabled={!canRedo}
                    aria-label="Redo"
                    className={classes.iconButton}
                  >
                    <RedoIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" className={classes.divider} />

            {/* Text formatting */}
            <Box className={classes.group}>
              <Tooltip title="Bold">
                <IconButton
                  size="small"
                  onClick={() => onFormatText("bold")}
                  aria-label="Bold"
                  className={classes.iconButton}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Italic">
                <IconButton
                  size="small"
                  onClick={() => onFormatText("italic")}
                  aria-label="Italic"
                  className={classes.iconButton}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Text Color">
                <IconButton
                  size="small"
                  onClick={handleColorClick}
                  aria-label="Text Color"
                  className={classes.iconButton}
                >
                  <FormatColorTextIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Insert Link">
                <IconButton
                  size="small"
                  onClick={handleLinkClick}
                  aria-label="Insert Link"
                  className={classes.iconButton}
                >
                  <LinkIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" className={classes.divider} />

            {/* Headings Menu */}
            <Box className={classes.group}>
              <Tooltip title="Headings">
                <IconButton
                  size="small"
                  onClick={handleHeadingMenuClick}
                  aria-controls="heading-menu"
                  aria-haspopup="true"
                  aria-label="Headings"
                  className={classes.iconButton}
                >
                  <FormatSizeIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Menu
                id="heading-menu"
                anchorEl={headingMenuAnchor}
                keepMounted
                open={Boolean(headingMenuAnchor)}
                onClose={handleHeadingMenuClose}
              >
                <MenuItem onClick={() => handleHeadingFormat("heading1")}>
                  Heading 1
                </MenuItem>
                <MenuItem onClick={() => handleHeadingFormat("heading2")}>
                  Heading 2
                </MenuItem>
                <MenuItem onClick={() => handleHeadingFormat("heading3")}>
                  Heading 3
                </MenuItem>
                <MenuItem onClick={() => handleHeadingFormat("paragraph")}>
                  Paragraph
                </MenuItem>
              </Menu>
            </Box>

            <Divider orientation="vertical" className={classes.divider} />

            {/* Alignment */}
            {!isMobile && (
              <Box className={classes.group}>
                <Tooltip title="Align Left">
                  <IconButton
                    size="small"
                    onClick={() => onFormatText("alignLeft")}
                    aria-label="Align Left"
                    className={classes.iconButton}
                  >
                    <FormatAlignLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Center">
                  <IconButton
                    size="small"
                    onClick={() => onFormatText("alignCenter")}
                    aria-label="Align Center"
                    className={classes.iconButton}
                  >
                    <FormatAlignCenterIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Right">
                  <IconButton
                    size="small"
                    onClick={() => onFormatText("alignRight")}
                    aria-label="Align Right"
                    className={classes.iconButton}
                  >
                    <FormatAlignRightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {isMobile && (
              <Box className={classes.group}>
                <Tooltip title="Alignment">
                  <IconButton
                    size="small"
                    onClick={handleAlignmentMenuClick}
                    aria-controls="alignment-menu"
                    aria-haspopup="true"
                    aria-label="Alignment"
                    className={classes.iconButton}
                  >
                    <FormatAlignLeftIcon fontSize="small" />{" "}
                    {/* Default icon */}
                  </IconButton>
                </Tooltip>
              </Box>
            )}

            <Divider orientation="vertical" className={classes.divider} />

            {/* Lists */}
            <Box className={classes.group}>
              <Tooltip title="Bullet List">
                <IconButton
                  size="small"
                  onClick={() => onFormatText("bullet")}
                  aria-label="Bullet List"
                  className={classes.iconButton}
                >
                  <FormatListBulletedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" className={classes.divider} />

            {/* Keyword highlighting */}
            <Box className={classes.group}>
              <Tooltip title="Highlight Keywords">
                <IconButton
                  size="small"
                  onClick={onToggleKeywordHighlight}
                  aria-label="Highlight Keywords"
                  className={`${classes.iconButton} ${
                    keywordHighlightEnabled ? classes.activeButton : ""
                  }`}
                >
                  <HighlightIcon
                    fontSize="small"
                    color={keywordHighlightEnabled ? "primary" : "inherit"}
                  />
                </IconButton>
              </Tooltip>
            </Box>

            <Divider orientation="vertical" className={classes.divider} />

            {/* AI Edit Button */}
            <Box className={classes.group}>
              <Tooltip title="Edit with AI">
                <span>
                  <IconButton
                    size="small"
                    onClick={handleOpenAIPrompt}
                    aria-label="Edit with AI"
                    className={classes.iconButton}
                    disabled={
                      isEditingWithAI ||
                      !getCurrentDocumentJson || // Use new prop name
                      !onUpdateDocumentFromJson // Use new prop name
                    }
                  >
                    {/* Container to manage positioning and size */}
                    <Box
                      position="relative"
                      display="inline-flex" // Or block, depending on desired layout
                      width={24} // Standard icon size
                      height={24} // Standard icon size
                      alignItems="center"
                      justifyContent="center"
                    >
                      {/* Icon is always rendered but hidden when loading */}
                      <AutorenewIcon
                        fontSize="small"
                        style={{
                          visibility: isEditingWithAI ? "hidden" : "visible",
                        }}
                      />
                      {/* Spinner is positioned absolutely on top when loading */}
                      {isEditingWithAI && (
                        <CircularProgress
                          size={20} // Slightly smaller than container to fit well
                          style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            marginTop: "-10px", // Half of size
                            marginLeft: "-10px", // Half of size
                          }}
                        />
                      )}
                    </Box>
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            {/* Mobile navigation controls - only shown on mobile - REMOVED */}
          </div>

          <div className={classes.actionsContainer}>
            {/* ATS Analysis button - Only render Tooltip and Button if onScrollToATS exists AND it's mobile view */}
            {onScrollToATS && showMobileControls && (
              <Button
                variant="contained" // Using contained for better visibility with purple background
                size="small"
                className={classes.atsButton}
                onClick={onScrollToATS}
                aria-label="View ATS Analysis"
                startIcon={<ArrowDownwardIcon fontSize="small" />}
              >
                ATS Analysis
              </Button>
            )}

            {/* Download button */}
            <Tooltip title="Download as Word">
              <span>
                {" "}
                {/* Wrapper for disabled state */}
                {showMobileControls ? (
                  <IconButton
                    color="primary"
                    size="small"
                    onClick={onDownload}
                    disabled={downloading || !hasContent}
                    className={classes.iconButton}
                    style={{ marginLeft: "8px" }}
                    aria-label="Download as Word"
                  >
                    {downloading ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <GetAppIcon fontSize="small" />
                    )}
                  </IconButton>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={
                      downloading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <GetAppIcon />
                      )
                    }
                    onClick={onDownload}
                    disabled={downloading || !hasContent}
                    className={classes.downloadButton}
                    style={{ marginLeft: "8px" }}
                  >
                    {downloading ? "Generating..." : "Download"}
                  </Button>
                )}
              </span>
            </Tooltip>
          </div>
        </div>

        {/* Link popover */}
        <LinkPopover
          anchorEl={linkPopoverAnchor}
          onClose={handleCloseLinkPopover}
          onInsertLink={handleInsertLink}
        />

        {/* Color picker */}
        <ColorPicker
          anchorEl={colorPickerAnchor}
          onClose={handleCloseColorPicker}
          onColorChange={handleColorChange}
        />

        {/* Alignment Menu */}
        <Menu
          id="alignment-menu"
          anchorEl={alignmentMenuAnchor}
          keepMounted
          open={Boolean(alignmentMenuAnchor)}
          onClose={handleAlignmentMenuClose}
        >
          <MenuItem onClick={() => handleAlignmentFormat("alignLeft")}>
            <FormatAlignLeftIcon fontSize="small" style={{ marginRight: 8 }} />{" "}
            Left
          </MenuItem>
          <MenuItem onClick={() => handleAlignmentFormat("alignCenter")}>
            <FormatAlignCenterIcon
              fontSize="small"
              style={{ marginRight: 8 }}
            />{" "}
            Center
          </MenuItem>
          <MenuItem onClick={() => handleAlignmentFormat("alignRight")}>
            <FormatAlignRightIcon fontSize="small" style={{ marginRight: 8 }} />{" "}
            Right
          </MenuItem>
        </Menu>

        {/* AI Prompt Dialog */}
        <Dialog
          open={aiPromptOpen}
          onClose={handleCloseAIPrompt}
          aria-labelledby="ai-edit-dialog-title"
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle id="ai-edit-dialog-title">
            Edit {documentType === "resume" ? "Resume" : "Cover Letter"} with AI
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              Describe the changes you'd like to make to your{" "}
              {documentType === "resume" ? "resume" : "cover letter"}. For
              example: "Make my professional summary more impactful..." or "Add
              'Project Management' to my skills..."
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="ai-prompt"
              label="Your Request"
              type="text"
              fullWidth
              multiline
              rows={4}
              variant="outlined"
              value={aiPromptText}
              onChange={(e) => setAiPromptText(e.target.value)}
              disabled={isEditingWithAI}
            />
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleCloseAIPrompt}
              color="primary"
              disabled={isEditingWithAI}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitAIPrompt}
              color="primary"
              disabled={isEditingWithAI || !aiPromptText.trim()}
            >
              {isEditingWithAI ? <CircularProgress size={24} /> : "Submit"}
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </>
  );
};

export default EditorToolbar;
